import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type ApiError } from '../../api/client';
import { Tile } from '../../components/Tile';

interface Dashboard {
  scope: string;
  seats: Array<{
    plan: string;
    purchased: number;
    allocated: number;
    available: number;
    endAt: string | null;
  }> | null;
  people: { active: number; inactive: number; invitedPending: number };
  learning: {
    enrolments: number;
    completed: number;
    completionRate: number;
    averagePercent: number;
  };
  mandatory: { assigned: number; completed: number; overdue: number };
  recentActivity: Array<{
    user: { id: string; name: string };
    course: string;
    percent: number;
    at: string | null;
  }>;
}

export function OrgDashboard() {
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api<Dashboard>('/org/dashboard')
      .then(setData)
      .catch((caught) => setError((caught as ApiError).message));
  }, []);

  if (error) {
    return (
      <div className="card">
        <div className="state">
          <div className="state__art" aria-hidden="true">⚠</div>
          <h4>This dashboard could not be loaded</h4>
          <p>{error}</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="spinner">Loading…</div>;

  const seats = data.seats?.[0] ?? null;

  return (
    <>
      <div className="page__head">
        <h1 className="page__title">Company Overview</h1>
        <p className="page__lede">
          You are seeing {data.scope}.
          {data.seats === null
            ? ' Seat figures are shown to organization administrators.'
            : ''}
        </p>
      </div>

      <div className="tiles">
        {seats ? (
          <Tile
            tone="azure"
            icon="◫"
            label="Seats available"
            value={String(seats.available)}
            note={`${seats.allocated} of ${seats.purchased} allocated`}
            progress={
              seats.purchased > 0 ? (seats.allocated / seats.purchased) * 100 : undefined
            }
          />
        ) : (
          <Tile
            tone="azure"
            icon="◍"
            label="Active people"
            value={String(data.people.active)}
          />
        )}
        <Tile
          tone="teal"
          icon="✓"
          label="Completion rate"
          value={`${data.learning.completionRate}%`}
          note={`${data.learning.completed} of ${data.learning.enrolments} enrolments`}
          progress={data.learning.completionRate}
        />
        <Tile
          /* Overdue work is the one figure here you want to notice, so it
             turns warm the moment it stops being zero. */
          tone={data.mandatory.overdue > 0 ? 'gold' : 'navy'}
          icon="!"
          label="Overdue mandatory"
          value={String(data.mandatory.overdue)}
          note={`${data.mandatory.assigned} assigned, ${data.mandatory.completed} done`}
        />
        <Tile
          tone="indigo"
          icon="◷"
          label="Average progress"
          value={`${data.learning.averagePercent}%`}
          note={data.people.invitedPending > 0
            ? `${data.people.invitedPending} invitations outstanding`
            : undefined}
          progress={data.learning.averagePercent}
        />
      </div>

      {seats ? (
        <div className="card">
          <div className="card__head">
            <span className="card__title">Seats</span>
            <span className="pill pill--inactive">{seats.plan}</span>
          </div>
          <div className="card__body">
            <div className="bar" aria-hidden="true">
              <i style={{ width: `${percentOf(seats.allocated, seats.purchased)}%` }} />
            </div>
            <p className="lcard__meta" style={{ marginTop: 'var(--sx-space-2)' }}>
              {seats.allocated} allocated · {seats.available} available
              {seats.endAt ? ` · term ends ${new Date(seats.endAt).toLocaleDateString()}` : ''}
            </p>
            {seats.available === 0 ? (
              <p className="lcard__locked" style={{ marginTop: 'var(--sx-space-3)' }}>
                Every seat is allocated. Someone accepting an invitation now will get an account
                but no access until a seat is freed or more are purchased.
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      <section className="section">
        <h2 className="section__title">Recent activity</h2>
        {data.recentActivity.length > 0 ? (
          <div className="card">
            <table className="table">
              <thead>
                <tr>
                  <th>Staff member</th>
                  <th>Course</th>
                  <th>Progress</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {data.recentActivity.map((row, index) => (
                  <tr key={`${row.user.id}-${index}`}>
                    <td>
                      <Link to={`/org/employees/${row.user.id}`}>{row.user.name}</Link>
                    </td>
                    <td>{row.course}</td>
                    <td>{row.percent}%</td>
                    <td className="muted">
                      {row.at ? new Date(row.at).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="card">
            <div className="state">
              <div className="state__art" aria-hidden="true">◎</div>
              <h4>No activity yet</h4>
              <p>It appears here as soon as someone opens a lesson.</p>
            </div>
          </div>
        )}
      </section>
    </>
  );
}

function percentOf(part: number, whole: number): number {
  return whole === 0 ? 0 : Math.round((part / whole) * 100);
}
